import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Gamepad2,
  Play,
  RotateCcw,
  Share2,
  Sparkles,
  Trophy,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { formatDate, timeAgo } from '../lib/format';
import {
  formatGamePeriodLabel,
  GameSharePopup,
  type GameQuizScope,
  type GameShareChannel,
} from '../components/games';

export const Route = createFileRoute('/_authed/games/$runId')({
  component: GameRunDetailPage,
});

// ---------------------------------------------------------------------------
// Response contract (defined locally — backend agent will land the matching
// schema in `@bbl/validation/games.ts` in parallel; when that import becomes
// available the local interface block here can be replaced with a single
// `import type { MyGameDetailResponse } from '@bbl/validation'`).
//
// Field names anchored to:
//   - docs/detail-pages-and-enrollment-plan.md §4.29 (the shared contract
//     both frontend and backend agents read from).
//   - apps/api/src/routes/me.ts:628 (the list endpoint), so attempt rows
//     and template/run column projections stay consistent.
//   - packages/db/src/schema/assessments.ts column names.
// ---------------------------------------------------------------------------

interface GameDetailTemplate {
  id: string;
  title: string;
  description: string | null;
  scope: GameQuizScope;
  /** Stable subject label (course name when course-owned). */
  subject: string;
  /** Number of questions served per attempt (`questionDrawCount` in DB). */
  drawCount: number;
  /** Optional pass mark in percent (template-level). */
  passThreshold: number | null;
  /** Whether the student is shown a score after submission. */
  showScoreToTaker: boolean;
}

interface GameDetailRun {
  id: string;
  /** ISO week (`2026-W22`) / month (`2026-05`) bucket, or null for ad-hoc. */
  periodKey: string | null;
  opensAt: string | null;
  closesAt: string | null;
}

interface GameDetailAttempt {
  id: string;
  attemptNo: number;
  status: 'in_progress' | 'submitted' | 'expired' | 'void';
  startedAt: string;
  submittedAt: string | null;
  scorePercent: number | null;
}

interface GameDetailShare {
  id: string;
  channel: GameShareChannel;
  shareUrl: string;
  referralCodeSnapshot: string;
  createdAt: string;
}

interface GameDetailLeaderboardEntry {
  initials: string;
  scorePercent: number;
  rank: number;
}

interface GameDetailLeaderboardSlice {
  top: GameDetailLeaderboardEntry[];
  yourRank: number | null;
  total: number;
}

interface MyGameDetailResponse {
  run: GameDetailRun;
  template: GameDetailTemplate;
  myAttempts: GameDetailAttempt[];
  myShares: GameDetailShare[];
  /**
   * Null when the parent template's `showScoreToTaker` is false (server
   * suppresses the slice in that case). Otherwise a top-N plus the caller's
   * rank-of-total.
   */
  leaderboardSlice: GameDetailLeaderboardSlice | null;
  /**
   * Remaining attempts under the cap. `null` is reserved for an unlimited
   * cap (`maxAttemptsPerUser <= 0`) — the UI surfaces "Unlimited" in that
   * branch and never disables the Play button.
   */
  attemptsRemaining: number | null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function GameRunDetailPage(): JSX.Element {
  const { runId } = Route.useParams();
  const role = useAuthStore((s) => s.user?.primaryRole);

  // Mirror the list page's role-gating posture: soft-check role in the
  // store so we render a friendly empty state instead of letting the API
  // 403 leak as a generic error. The endpoint itself is `requireRoles
  // ('student')` on the backend.
  const detailQuery = useQuery({
    queryKey: ['games', 'detail', runId] as const,
    queryFn: ({ signal }) =>
      apiRequest<MyGameDetailResponse>(
        `/api/me/games/${encodeURIComponent(runId)}`,
        { signal },
      ),
    enabled: role === 'student',
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 1;
    },
  });

  if (role !== 'student') {
    return (
      <ListShell title="Quiz">
        <EmptyState
          title="Games are for students"
          description="Switch to a student account to see this quiz."
          icon={<Gamepad2 size={32} aria-hidden="true" />}
          action={
            <Link to="/games">
              <Button variant="secondary">Back to games</Button>
            </Link>
          }
        />
      </ListShell>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Games"
        description="Weekly quizzes, streaks, and bragging rights — coming as soon as the academy switches the Games module on."
        onRetry={() => {
          void detailQuery.refetch();
        }}
      >
        {detailQuery.isLoading || !detailQuery.data ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <DetailBody detail={detailQuery.data} />
        )}
      </GatedSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function DetailBody({ detail }: { detail: MyGameDetailResponse }): JSX.Element {
  const { run, template, myAttempts, myShares, leaderboardSlice } = detail;
  const periodLabel = formatGamePeriodLabel(template.scope, run.periodKey);
  const cadenceLabel = template.scope === 'weekly_quiz' ? 'Weekly' : 'Monthly';

  const inProgressAttempt = useMemo(
    () => myAttempts.find((a) => a.status === 'in_progress') ?? null,
    [myAttempts],
  );
  // Best score across finished attempts. We treat `expired` and `submitted`
  // as "finished"; a void attempt does not contribute to the best.
  const finishedAttempts = useMemo(
    () =>
      myAttempts.filter(
        (a) => a.status === 'submitted' || a.status === 'expired',
      ),
    [myAttempts],
  );
  const bestAttempt = useMemo(() => {
    let best: GameDetailAttempt | null = null;
    for (const a of finishedAttempts) {
      if (a.scorePercent === null) continue;
      if (best === null || (a.scorePercent ?? -1) > (best.scorePercent ?? -1)) {
        best = a;
      }
    }
    return best;
  }, [finishedAttempts]);

  // Share again uses the most recent finished attempt (the one the student
  // most likely remembers). Falls back to the best when there's no
  // finished attempt with a `submittedAt` ordering hint.
  const shareAttempt = useMemo(() => {
    if (finishedAttempts.length === 0) return null;
    const sorted = [...finishedAttempts].sort((a, b) => {
      const aTs = a.submittedAt ?? a.startedAt;
      const bTs = b.submittedAt ?? b.startedAt;
      if (aTs > bTs) return -1;
      if (aTs < bTs) return 1;
      return 0;
    });
    return sorted[0] ?? null;
  }, [finishedAttempts]);

  // Attempts-left semantics — `null` means an unlimited cap (the backend
  // signals this when `maxAttemptsPerUser <= 0`). Plain `0` disables Play.
  const attemptsRemaining = detail.attemptsRemaining;
  const attemptsExhausted = attemptsRemaining === 0;
  const playMode: 'play' | 'resume' | 'replay' = inProgressAttempt
    ? 'resume'
    : finishedAttempts.length > 0
      ? 'replay'
      : 'play';

  // Attempt-status chip on the header reflects the student's progress on
  // this run, not the run's open/closed lifecycle (which is implicit —
  // the detail page only fetches open runs).
  const attemptStatusChip = (
    <AttemptStatusChip
      mode={playMode}
      attemptsExhausted={attemptsExhausted}
    />
  );

  const kpiItems = useMemo<KpiItem[]>(() => {
    const best = bestAttempt?.scorePercent ?? null;
    const closesInLabel = formatClosesIn(run.closesAt);
    const attemptsLabel =
      attemptsRemaining === null
        ? 'Unlimited'
        : String(attemptsRemaining);
    return [
      {
        label: 'Your best',
        value: best === null ? '—' : `${best}%`,
        sub: best === null ? 'no scores yet' : undefined,
      },
      {
        label: 'Attempts left',
        value: attemptsLabel,
        sub:
          attemptsRemaining !== null && attemptsRemaining > 0
            ? attemptsRemaining === 1
              ? '1 attempt'
              : `${attemptsRemaining} attempts`
            : undefined,
      },
      {
        label: 'Closes in',
        value: closesInLabel.value,
        sub: closesInLabel.sub,
      },
      {
        label: 'Questions',
        value: template.drawCount,
        sub: template.drawCount === 1 ? 'one question' : undefined,
      },
    ];
  }, [bestAttempt, run.closesAt, attemptsRemaining, template.drawCount]);

  // Primary CTA — disabled only when the cap has been reached.
  const primaryAction = (
    <PrimaryCta
      mode={playMode}
      runId={run.id}
      disabled={attemptsExhausted}
    />
  );

  return (
    <div className="space-y-6">
      <DetailHeader
        title={template.subject || template.title}
        sublabel={
          <>
            <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
              <Sparkles
                size={12}
                aria-hidden="true"
                className="text-gold-600"
              />
              {periodLabel}
            </span>
            <ScopeChip scope={template.scope} />
            {attemptStatusChip}
          </>
        }
        statusChip={null}
        backTo="/games"
        actions={primaryAction}
      />

      <KpiStrip items={kpiItems} />

      <AboutCard
        template={template}
        cadenceLabel={cadenceLabel}
        periodLabel={periodLabel}
      />

      <YourAttemptsCard attempts={myAttempts} />

      {leaderboardSlice ? (
        <LeaderboardCard slice={leaderboardSlice} />
      ) : null}

      <StreakPreviewCard
        playedCount={finishedAttempts.length}
        cadenceLabel={cadenceLabel}
      />

      <ShareYourWinCard
        shares={myShares}
        shareAttempt={shareAttempt}
        quizTitle={template.subject || template.title}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header sub-bits
// ---------------------------------------------------------------------------

function AttemptStatusChip({
  mode,
  attemptsExhausted,
}: {
  mode: 'play' | 'resume' | 'replay';
  attemptsExhausted: boolean;
}): JSX.Element {
  if (attemptsExhausted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
        No attempts left
      </span>
    );
  }
  if (mode === 'resume') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-info)]/40 bg-[color:var(--color-info)]/10 px-2 py-0.5 text-xs font-semibold text-[color:var(--color-info)]">
        Attempt in progress
      </span>
    );
  }
  if (mode === 'replay') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-xs font-semibold text-[color:var(--color-success)]">
        <Trophy size={11} aria-hidden="true" />
        Already played
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 bg-gold-500/10 px-2 py-0.5 text-xs font-semibold text-gold-700">
      Ready to play
    </span>
  );
}

function ScopeChip({ scope }: { scope: GameQuizScope }): JSX.Element {
  const label = scope === 'weekly_quiz' ? 'Weekly quiz' : 'Monthly quiz';
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
      {label}
    </span>
  );
}

function PrimaryCta({
  mode,
  runId,
  disabled,
}: {
  mode: 'play' | 'resume' | 'replay';
  runId: string;
  disabled: boolean;
}): JSX.Element {
  const label =
    mode === 'resume' ? 'Resume' : mode === 'replay' ? 'Play again' : 'Play';
  const icon =
    mode === 'resume' ? (
      <RotateCcw size={16} aria-hidden="true" />
    ) : (
      <Play size={16} aria-hidden="true" />
    );

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        aria-disabled="true"
        className="inline-flex h-10 items-center gap-1.5 rounded-full bg-gold-500/40 px-5 text-sm font-semibold text-ink-900/60 shadow-sm"
      >
        {icon}
        {label}
      </button>
    );
  }
  return (
    <Link
      to="/assessments/runs/$runId/start"
      params={{ runId }}
      aria-label={label}
      className={clsx(
        'inline-flex h-10 items-center gap-1.5 rounded-full px-5 text-sm font-semibold transition-colors',
        'bg-gold-500 text-ink-900 shadow-sm hover:bg-gold-400',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
        'no-underline',
      )}
    >
      {icon}
      {label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Section: About this quiz
// ---------------------------------------------------------------------------

function AboutCard({
  template,
  cadenceLabel,
  periodLabel,
}: {
  template: GameDetailTemplate;
  cadenceLabel: string;
  periodLabel: string;
}): JSX.Element {
  return (
    <section
      aria-labelledby="games-about-heading"
      className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 shadow-sm"
    >
      <h2
        id="games-about-heading"
        className="font-display text-lg font-semibold text-[color:var(--color-text)]"
      >
        About this quiz
      </h2>
      <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
        {template.title}
      </p>
      {template.description ? (
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[color:var(--color-text)]">
          {template.description}
        </p>
      ) : null}
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <FactCell label="Scope" value={cadenceLabel} />
        <FactCell label="Cadence" value={periodLabel} />
        <FactCell
          label="Pass mark"
          value={
            template.passThreshold === null
              ? 'No threshold'
              : `${template.passThreshold}%`
          }
        />
        <FactCell
          label="Score shown"
          value={template.showScoreToTaker ? 'Yes' : 'Hidden'}
        />
      </dl>
    </section>
  );
}

function FactCell({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-2">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium text-[color:var(--color-text)]">
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Your attempts
// ---------------------------------------------------------------------------

function YourAttemptsCard({
  attempts,
}: {
  attempts: GameDetailAttempt[];
}): JSX.Element {
  return (
    <section
      aria-labelledby="games-attempts-heading"
      className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="games-attempts-heading"
          className="font-display text-lg font-semibold text-[color:var(--color-text)]"
        >
          Your attempts
        </h2>
        <span className="text-xs text-[color:var(--color-text-muted)]">
          {attempts.length}{' '}
          {attempts.length === 1 ? 'attempt' : 'attempts'}
        </span>
      </header>

      {attempts.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-4 text-center text-sm text-[color:var(--color-text-muted)]">
          You haven&apos;t played this one yet. Tap Play above to start.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-medium uppercase tracking-wide text-[color:var(--color-text-muted)]">
                <th className="px-2 py-2">#</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Started</th>
                <th className="px-2 py-2">Submitted</th>
                <th className="px-2 py-2 text-right">Score</th>
                <th className="px-2 py-2" aria-label="Open result"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-border)]">
              {attempts.map((a) => (
                <tr key={a.id} className="hover:bg-[color:var(--color-surface)]">
                  <td className="px-2 py-2 tabular-nums text-[color:var(--color-text-muted)]">
                    {a.attemptNo}
                  </td>
                  <td className="px-2 py-2">
                    <AttemptRowStatus status={a.status} />
                  </td>
                  <td className="px-2 py-2 text-[color:var(--color-text-muted)]">
                    {formatDate(a.startedAt, { withTime: true })}
                  </td>
                  <td className="px-2 py-2 text-[color:var(--color-text-muted)]">
                    {a.submittedAt
                      ? formatDate(a.submittedAt, { withTime: true })
                      : '—'}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums font-medium text-[color:var(--color-text)]">
                    {a.scorePercent === null ? '—' : `${a.scorePercent}%`}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Link
                      to="/assessments/attempts/$attemptId/result"
                      params={{ attemptId: a.id }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AttemptRowStatus({
  status,
}: {
  status: GameDetailAttempt['status'];
}): JSX.Element {
  const config: Record<
    GameDetailAttempt['status'],
    { label: string; cls: string }
  > = {
    in_progress: {
      label: 'In progress',
      cls: 'border-[color:var(--color-info)]/40 bg-[color:var(--color-info)]/10 text-[color:var(--color-info)]',
    },
    submitted: {
      label: 'Submitted',
      cls: 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]',
    },
    expired: {
      label: 'Expired',
      cls: 'border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/10 text-[color:var(--color-warning)]',
    },
    void: {
      label: 'Voided',
      cls: 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
    },
  };
  const cfg = config[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        cfg.cls,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section: Leaderboard
// ---------------------------------------------------------------------------

function LeaderboardCard({
  slice,
}: {
  slice: GameDetailLeaderboardSlice;
}): JSX.Element {
  return (
    <section
      aria-labelledby="games-leaderboard-heading"
      className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="games-leaderboard-heading"
          className="font-display text-lg font-semibold text-[color:var(--color-text)]"
        >
          Leaderboard
        </h2>
        <span className="text-xs text-[color:var(--color-text-muted)]">
          Top {slice.top.length}
        </span>
      </header>
      {slice.top.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-4 text-center text-sm text-[color:var(--color-text-muted)]">
          No scores on the board yet. Be the first!
        </p>
      ) : (
        <ol className="mt-3 space-y-2">
          {slice.top.map((entry) => (
            <li
              key={`${entry.rank}-${entry.initials}`}
              className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  aria-hidden="true"
                  className={clsx(
                    'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums',
                    entry.rank === 1
                      ? 'bg-gold-500/20 text-gold-700'
                      : entry.rank === 2
                        ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
                        : entry.rank === 3
                          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]'
                          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
                  )}
                >
                  {entry.rank}
                </span>
                <span className="font-mono text-sm font-semibold text-[color:var(--color-text)]">
                  {entry.initials}
                </span>
              </div>
              <span className="tabular-nums text-sm font-semibold text-[color:var(--color-text)]">
                {entry.scorePercent}%
              </span>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
        {slice.yourRank === null ? (
          <>
            You haven&apos;t scored on this one yet — play to claim your
            spot of {slice.total}.
          </>
        ) : (
          <>
            You:{' '}
            <span className="font-semibold text-[color:var(--color-text)]">
              rank #{slice.yourRank}
            </span>{' '}
            of {slice.total}.
          </>
        )}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: Streak preview
// ---------------------------------------------------------------------------

function StreakPreviewCard({
  playedCount,
  cadenceLabel,
}: {
  playedCount: number;
  cadenceLabel: string;
}): JSX.Element {
  const headline =
    playedCount === 0
      ? 'Start your streak'
      : playedCount === 1
        ? 'You’re off the mark!'
        : `You’ve played ${playedCount} times`;
  const sub =
    playedCount === 0
      ? `Your first ${cadenceLabel.toLowerCase()} quiz attempt starts the streak. Keep showing up.`
      : playedCount === 1
        ? `One in the bag. Beat this score next ${cadenceLabel.toLowerCase()} to keep the streak alive.`
        : `Consistency wins. Stack another finish to grow your ${cadenceLabel.toLowerCase()} streak.`;

  return (
    <section
      aria-labelledby="games-streak-heading"
      className="relative overflow-hidden rounded-2xl border border-gold-500/30 bg-gradient-to-br from-gold-500/10 via-[color:var(--color-bg-elevated)] to-[color:var(--color-bg-elevated)] p-5 shadow-sm"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gold-500/15 blur-2xl"
      />
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-500/20 text-gold-700"
        >
          <Sparkles size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2
            id="games-streak-heading"
            className="font-display text-lg font-semibold text-[color:var(--color-text)]"
          >
            {headline}
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            {sub}
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: Share your win
// ---------------------------------------------------------------------------

function ShareYourWinCard({
  shares,
  shareAttempt,
  quizTitle,
}: {
  shares: GameDetailShare[];
  shareAttempt: GameDetailAttempt | null;
  quizTitle: string;
}): JSX.Element {
  const [popupOpen, setPopupOpen] = useState(false);

  return (
    <section
      aria-labelledby="games-shares-heading"
      className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="games-shares-heading"
          className="font-display text-lg font-semibold text-[color:var(--color-text)]"
        >
          Share your win
        </h2>
        {shareAttempt ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<Share2 size={14} aria-hidden="true" />}
            onClick={() => setPopupOpen(true)}
          >
            Share again
          </Button>
        ) : (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            Finish an attempt to share
          </span>
        )}
      </header>

      {shares.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-4 text-center text-sm text-[color:var(--color-text-muted)]">
          No shares yet. Win the quiz, tell your friends — get them on the
          team.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {shares.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <ChannelChip channel={s.channel} />
                <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
                  {s.referralCodeSnapshot}
                </span>
              </div>
              <span
                className="text-xs text-[color:var(--color-text-muted)]"
                title={formatDate(s.createdAt, { withTime: true })}
              >
                {timeAgo(s.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {shareAttempt ? (
        <GameSharePopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          attemptId={shareAttempt.id}
          scorePercent={shareAttempt.scorePercent}
          quizTitle={quizTitle}
        />
      ) : null}
    </section>
  );
}

function ChannelChip({
  channel,
}: {
  channel: GameShareChannel;
}): JSX.Element {
  const label: Record<GameShareChannel, string> = {
    whatsapp_chat: 'WhatsApp chat',
    whatsapp_status: 'WhatsApp status',
    other_link_copy: 'Link copied',
    other_native_share: 'Native share',
  };
  return (
    <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
      {label[channel]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Render the "Closes in" KPI. Three branches:
 *   - null/unparseable → "No deadline" / blank sub.
 *   - already past → "Closed" / when.
 *   - future → human-friendly delta from now ("in 2d", "in 5h", "in 12m").
 *
 * Inline instead of leaning on `timeAgo` because the existing helper is
 * past-tense only ("12m ago"); we want future-tense for an upcoming
 * deadline.
 */
function formatClosesIn(closesAt: string | null): {
  value: string;
  sub: string | undefined;
} {
  if (!closesAt) {
    return { value: 'No deadline', sub: undefined };
  }
  const ts = Date.parse(closesAt);
  if (!Number.isFinite(ts)) {
    return { value: 'No deadline', sub: undefined };
  }
  const diff = ts - Date.now();
  if (diff <= 0) {
    return {
      value: 'Closed',
      sub: formatDate(closesAt, { withTime: true }),
    };
  }
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) {
    return {
      value: `${Math.max(1, minutes)}m`,
      sub: formatDate(closesAt, { withTime: true }),
    };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return {
      value: `${hours}h`,
      sub: formatDate(closesAt, { withTime: true }),
    };
  }
  const days = Math.floor(hours / 24);
  if (days < 14) {
    return {
      value: `${days}d`,
      sub: formatDate(closesAt, { withTime: true }),
    };
  }
  return {
    value: formatDate(closesAt),
    sub: formatDate(closesAt, { withTime: true }),
  };
}
